/**
 * API client — thin wrapper around fetch that talks to our FastAPI backend.
 *
 * All functions return plain JS objects (already parsed from JSON).
 * Errors throw with a message you can display in the UI.
 *
 * The Vite proxy forwards /api/* requests to http://localhost:8000/api/*
 * so we don't need to hardcode the backend URL here.
 *
 * AUTH: Every request automatically includes the Supabase JWT token in an
 * Authorization header. The backend validates this to identify who is calling.
 */
import { supabase } from '../lib/supabase'

// In development, Vite proxies /api → localhost:8001 (see vite.config.js).
// In production (Vercel), the vercel.json rewrites handle routing /api → Railway.
// We keep BASE as '/api' so both environments work without any code changes.
const BASE = '/api'

/**
 * Get the Authorization header with the current user's JWT.
 * If the user is not signed in, returns an empty object (the backend will 401).
 * Supabase refreshes expired tokens automatically — this always gets the latest.
 */
async function getAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return {}
  return { 'Authorization': `Bearer ${session.access_token}` }
}

async function request(path, options = {}) {
  const authHeader = await getAuthHeader()
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeader, ...options.headers },
    ...options,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(err.detail || `HTTP ${res.status}`)
  }

  // Handle empty responses (DELETE returns {"ok": true})
  const text = await res.text()
  return text ? JSON.parse(text) : null
}

// ── Groups ───────────────────────────────────────────────────────────────────
export const api = {
  // Groups
  getGroups: () => request('/groups'),
  // createGroup accepts optional trip dates, base_currency, and group kind.
  // kind: 'trip' (default — has dates, settles at end) or 'household'
  // (ongoing roommate / couple ledger — no dates, recurring expenses, running balance).
  createGroup: (name, start_date = null, end_date = null, base_currency = 'USD', kind = 'trip') =>
    request('/groups', { method: 'POST', body: JSON.stringify({ name, start_date, end_date, base_currency, kind }) }),
  updateGroup: (id, name, start_date = null, end_date = null) =>
    request(`/groups/${id}`, { method: 'PUT', body: JSON.stringify({ name, start_date, end_date }) }),
  deleteGroup: (id) => request(`/groups/${id}`, { method: 'DELETE' }),
  getGroup: (id) => request(`/groups/${id}`),

  // Members
  // Add a member to the trip. The simple form (just a name) covers 90% of
  // calls. The opts object lets the People sheet pre-link a known person from
  // your trip history (user_id) and carry over their payment handles.
  addMember: (groupId, name, opts = {}) => request(`/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify({
      name,
      user_id: opts.user_id ?? null,
      payment_handles: opts.payment_handles ?? null,
    }),
  }),

  // People you've collaborated with on past trips — surfaced as quick-add
  // chips in the People sheet so you don't retype a name for every new trip.
  // Pass currentGroupId so the trip you're editing is excluded from the
  // pool (otherwise its own members would show up as past collaborators
  // of themselves).
  getRecentCollaborators: (currentGroupId) => request(
    '/me/recent-collaborators' + (currentGroupId ? `?current_group_id=${currentGroupId}` : '')
  ),

  // Reserve N "(Pending)" placeholder member slots and get the invite link.
  // The UI calls this with the slider value the owner sets in the People sheet.
  // Idempotent: pass the *target* total, not a delta.
  createInviteSlots: (groupId, count) => request(`/groups/${groupId}/invite-slots`, {
    method: 'POST', body: JSON.stringify({ count }),
  }),

  // "Where I stand" balance for the trip overview header. Returns the current
  // user's net position (positive = owed, negative = you owe) plus paid/share
  // breakdown. Returns linked:false if user hasn't claimed a member slot yet.
  getMyBalance: (groupId) => request(`/groups/${groupId}/my-balance`),

  // Link the current signed-in user to an existing unclaimed member row.
  // Used by the People sheet's "Claim as me" button.
  claimMemberSlot: (memberId) => request(`/members/${memberId}/claim`, {
    method: 'PUT',
  }),

  // Break a transaction into per-item splits (mixed receipts/statements).
  // items: [{name, amount, member_ids}]. Sum must equal txn amount.
  // Pass an empty array to clear itemization (revert to whole-txn split).
  setTransactionItems: (txnId, items) => request(`/transactions/${txnId}/items`, {
    method: 'PUT', body: JSON.stringify({ items }),
  }),

  // ── Recurring expenses (households / monthly bills) ───────────────────────
  // Templates that auto-generate transactions monthly. The list call also
  // implicitly catches up missing past instances on the backend (lazy cron).
  listRecurring: (groupId) => request(`/groups/${groupId}/recurring`),
  createRecurring: (groupId, body) => request(`/groups/${groupId}/recurring`, {
    method: 'POST', body: JSON.stringify(body),
  }),
  toggleRecurring: (recurringId) => request(`/recurring/${recurringId}/toggle`, {
    method: 'PUT',
  }),
  deleteRecurring: (recurringId) => request(`/recurring/${recurringId}`, {
    method: 'DELETE',
  }),
  updateMember: (id, name) => request(`/members/${id}`, {
    method: 'PUT', body: JSON.stringify({ name }),
  }),
  deleteMember: (id) => request(`/members/${id}`, { method: 'DELETE' }),

  // Update a member's payment handles (Venmo, Cash App, PayPal, Zelle).
  // Pass an object like { venmo: 'anthony-w', paypal: '...' } — server normalises
  // (strips @ and $ prefixes) and stores. Returns the updated member.
  updatePaymentHandles: (memberId, handles) => request(`/members/${memberId}/payment-handles`, {
    method: 'PUT', body: JSON.stringify(handles),
  }),

  // Receipt OCR — upload a receipt photo, get back structured fields the
  // Add Expense form can pre-fill. Returns: { amount, currency, merchant,
  // posted_date, category, items, confidence, notes }. Throws on parse
  // failure; the modal will surface the message to the user.
  // We hand-roll the fetch (instead of using `request`) because we need to
  // send multipart/form-data and skip the JSON content-type header.
  parseReceipt: async (file) => {
    const fd = new FormData()
    fd.append('file', file)
    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/receipts/parse`, {
      method: 'POST',
      headers: { ...authHeader },     // do NOT set Content-Type — browser handles it for FormData
      body: fd,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.detail || `Receipt parsing failed (HTTP ${res.status})`)
    }
    return res.json()
  },

  // Statements
  getStatements: (groupId) => request(`/groups/${groupId}/statements`),
  deleteStatement: (id) => request(`/statements/${id}`, { method: 'DELETE' }),

  // Rename a statement ("Sapphire (trip card)"). Empty string clears the
  // nickname back to the automatic "Bank ···last4" label.
  renameStatement: (id, nickname) => request(`/statements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ nickname }),
  }),

  // Attach (or replace) the original document on an existing statement —
  // for statements imported before originals were stored. The backend also
  // backfills the filename and the card's last-4 from PDFs.
  attachStatementOriginal: async (id, file) => {
    const form = new FormData()
    form.append('file', file)
    const authHeader = await getAuthHeader()
    // No Content-Type header — the browser sets the multipart boundary.
    const res = await fetch(`${BASE}/statements/${id}/original`, {
      method: 'PUT',
      headers: authHeader,
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(err.detail || `HTTP ${res.status}`)
    }
    return res.json()
  },

  // Fetch the original uploaded document as a Blob. A plain <a href> can't
  // carry the Authorization header, so callers fetch the blob and open an
  // object URL instead.
  fetchStatementOriginal: async (id) => {
    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/statements/${id}/original`, { headers: authHeader })
    if (!res.ok) throw new Error('Could not load the original document')
    return res.blob()
  },
  setCardHolder: (statementId, memberId) => request(`/statements/${statementId}/card-holder`, {
    method: 'PUT', body: JSON.stringify({ member_id: memberId }),
  }),

  // Upload Chase PDF statement
  // statementCurrency: the currency on the card (e.g. "CAD" for a Canadian card)
  // exchangeRate: 1 statementCurrency = exchangeRate baseCurrency (e.g. 0.74 for CAD→USD)
  uploadPDF: async (groupId, file, cardHolderMemberId, statementCurrency = 'USD', exchangeRate = null) => {
    const form = new FormData()
    form.append('file', file)
    if (cardHolderMemberId) form.append('card_holder_member_id', cardHolderMemberId)
    form.append('statement_currency', statementCurrency)
    if (exchangeRate) form.append('exchange_rate', exchangeRate)

    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/groups/${groupId}/statements/upload`, {
      method: 'POST',
      // IMPORTANT: Do NOT set Content-Type here — the browser sets it automatically
      // for FormData including the correct boundary string. Setting it manually breaks uploads.
      headers: authHeader,
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(err.detail || `Upload failed: HTTP ${res.status}`)
    }
    return res.json()
  },

  // Upload a bank CSV export (Chase, Amex, BofA, Citi, Capital One, Discover)
  // Bank format is auto-detected from the CSV headers.
  uploadCSV: async (groupId, file, cardHolderMemberId, statementCurrency = 'USD', exchangeRate = null) => {
    const form = new FormData()
    form.append('file', file)
    if (cardHolderMemberId) form.append('card_holder_member_id', cardHolderMemberId)
    form.append('statement_currency', statementCurrency)
    if (exchangeRate) form.append('exchange_rate', exchangeRate)

    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/groups/${groupId}/statements/upload-csv`, {
      method: 'POST',
      headers: authHeader,  // same note: no Content-Type for FormData
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(err.detail || `Upload failed: HTTP ${res.status}`)
    }
    return res.json()
  },

  // Upload a bank-app SCREENSHOT (image) — OCR extracts every transaction row.
  // Mirrors uploadPDF but hits the parse-screenshot endpoint. Optional currency
  // behaves like the statement uploads (foreign currency needs an exchange rate
  // or the backend returns 400).
  parseScreenshot: async (groupId, file, cardHolderMemberId, statementCurrency = 'USD', exchangeRate = null) => {
    const form = new FormData()
    form.append('file', file)
    if (cardHolderMemberId) form.append('card_holder_member_id', cardHolderMemberId)
    form.append('statement_currency', statementCurrency)
    if (exchangeRate) form.append('exchange_rate', exchangeRate)
    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/groups/${groupId}/statements/parse-screenshot`, {
      method: 'POST',
      // IMPORTANT: Do NOT set Content-Type here — the browser sets it automatically
      // for FormData including the correct boundary string. Setting it manually breaks uploads.
      headers: authHeader,
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(err.detail || `Screenshot import failed: HTTP ${res.status}`)
    }
    return res.json()
  },

  // Manually add a single expense (no statement upload needed)
  // paidByMemberId: which member paid; participantIds: array of member IDs who split it
  // currency: what currency the charge was in (e.g. "JPY") — defaults to "USD"
  // originalAmount: the amount in the foreign currency (set automatically if currency differs)
  // exchangeRate: how many base-currency units 1 foreign-currency unit equals (e.g. 0.0067)
  createManualTransaction: (groupId, { postedDate, description, amount, paidByMemberId, category, participantIds, splitMethod, currency, exchangeRate }) =>
    request(`/groups/${groupId}/transactions/manual`, {
      method: 'POST',
      body: JSON.stringify({
        posted_date: postedDate,
        description,
        amount: parseFloat(amount),
        paid_by_member_id: paidByMemberId,
        category: category || null,
        // Build the participants_json in the format the backend expects
        participants_json: participantIds?.length
          ? { type: 'custom', member_ids: participantIds }
          : null,
        split_method_json: splitMethod || { type: 'equal' },
        // Multi-currency fields — backend converts amount if currency != base_currency
        currency: currency || 'USD',
        exchange_rate: exchangeRate ? parseFloat(exchangeRate) : null,
      }),
    }),

  // Transactions
  getTransactions: (statementId) => request(`/statements/${statementId}/transactions`),
  getGroupTransactions: (groupId) => request(`/groups/${groupId}/transactions`),
  updateTransaction: (id, updates) => request(`/transactions/${id}`, {
    method: 'PUT', body: JSON.stringify(updates),
  }),
  deleteTransaction: (id) => request(`/transactions/${id}`, { method: 'DELETE' }),
  saveMerchantRule: (transactionId, merchantKey) => request('/transactions/save-merchant-rule', {
    method: 'POST', body: JSON.stringify({ transaction_id: transactionId, merchant_key: merchantKey }),
  }),
  batchUpdate: (statementId, updates) => request(`/statements/${statementId}/batch-update`, {
    method: 'POST', body: JSON.stringify(updates),
  }),
  // Bulk-update multiple transactions at once — the core trip workflow
  bulkUpdateTransactions: (groupId, body) =>
    request(`/groups/${groupId}/transactions/bulk-update`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // Merchant rules
  getMerchantRules: (groupId) => request(`/groups/${groupId}/merchant-rules`),
  deleteMerchantRule: (id) => request(`/merchant-rules/${id}`, { method: 'DELETE' }),

  // Settlement
  computeSettlement: (groupId, payerMemberId, statementId = null) =>
    request(`/groups/${groupId}/settlement`, {
      method: 'POST',
      body: JSON.stringify({ payer_member_id: payerMemberId, statement_id: statementId }),
    }),

  // Export all transactions (date, merchant, category, amount, participants, status)
  // as a CSV you can open in Google Sheets or Excel — no settlement computation needed.
  exportTransactionsCSV: async (groupId, groupName) => {
    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/groups/${groupId}/transactions/export-csv`, {
      headers: authHeader,
    })
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    // Use the group name in the filename so it's easy to find in Downloads
    a.download = `${groupName.replace(/\s+/g, '_')}_transactions.csv`
    a.click()
    URL.revokeObjectURL(url)
  },

  exportCSV: async (groupId, payerMemberId, statementId = null) => {
    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/groups/${groupId}/settlement/export-csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify({ payer_member_id: payerMemberId, statement_id: statementId }),
    })
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'settlement.csv'
    a.click()
    URL.revokeObjectURL(url)
  },

  // ── Trip Share ───────────────────────────────────────────────────────────
  // Create (or return existing) share link for a trip.
  // payerMemberId: the member currently selected as the payer on the settlement page.
  // Returns { share_code, share_url, created_at, view_count }
  createShare: (groupId, payerMemberId) =>
    request(`/groups/${groupId}/share`, {
      method: 'POST',
      body: JSON.stringify({ payer_member_id: payerMemberId }),
    }),

  // Remove the share link — link immediately stops working for anyone who has it.
  revokeShare: (groupId) => request(`/groups/${groupId}/share`, { method: 'DELETE' }),

  // Fetch the public trip view — NO auth required.
  // This is what visitors see when they open a share link.
  getPublicShare: (shareCode) => request(`/share/${shareCode}`),

  // Submit in-app feedback
  submitFeedback: (feedbackType, message, email = null, page = null) =>
    request('/feedback', {
      method: 'POST',
      body: JSON.stringify({ feedback_type: feedbackType, message, email, page }),
    }),

  // ── Trip Invites ──────────────────────────────────────────────────────────
  // Get (or generate) the invite link for a trip you own.
  // Returns { invite_code, invite_url }
  getInviteLink: (groupId) => request(`/groups/${groupId}/invite-link`),

  // Preview a trip from an invite link — no auth required.
  // Returns { trip_name, start_date, end_date, member_count, unclaimed_members, invite_code }
  getInvitePreview: (inviteCode) => request(`/invite/${inviteCode}`),

  // Join a trip via invite link — auth required.
  // claimMemberId: id of an existing unclaimed member to claim (or null)
  // newName: join as a brand new member with this name (or null)
  joinTrip: (inviteCode, { claimMemberId, newName } = {}) =>
    request(`/invite/${inviteCode}/join`, {
      method: 'POST',
      body: JSON.stringify({
        member_id: claimMemberId || null,
        name: newName || null,
      }),
    }),

  exportJSON: async (groupId, payerMemberId, statementId = null) => {
    const authHeader = await getAuthHeader()
    const res = await fetch(`${BASE}/groups/${groupId}/settlement/export-json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify({ payer_member_id: payerMemberId, statement_id: statementId }),
    })
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'settlement.json'
    a.click()
    URL.revokeObjectURL(url)
  },
}
